import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useNotificacoesStore } from '@/stores/notificacoes';

/**
 * Infinite scroll do painel de notificações via IntersectionObserver.
 * Quando o sentinela entra na área visível do container, carrega a próxima página.
 *
 * @param {import('vue').Ref<HTMLElement|null>} scrollRootRef
 * @param {import('vue').Ref<boolean>|(() => boolean)} [isActive]
 */
export function useNotificationInfiniteScroll(scrollRootRef, isActive = true) {
    const notificacoesStore = useNotificacoesStore();
    const sentinelRef = ref(null);
    let observer = null;

    const resolveActive = () => {
        if (typeof isActive === 'function') return Boolean(isActive());
        if (isActive && typeof isActive === 'object' && 'value' in isActive) {
            return Boolean(isActive.value);
        }
        return Boolean(isActive);
    };

    const teardown = () => {
        if (observer) {
            observer.disconnect();
            observer = null;
        }
    };

    const setup = async () => {
        teardown();
        await nextTick();

        if (!resolveActive() || !sentinelRef.value) {
            return;
        }

        observer = new IntersectionObserver(
            (entries) => {
                if (!entries.some((entry) => entry.isIntersecting)) {
                    return;
                }
                notificacoesStore.loadMoreNotifications().catch(() => {});
            },
            {
                root: scrollRootRef.value || null,
                rootMargin: '120px 0px',
                threshold: 0,
            }
        );

        observer.observe(sentinelRef.value);
    };

    watch(
        [
            () => resolveActive(),
            () => notificacoesStore.hasMore,
            () => notificacoesStore.notifications.length,
            () => scrollRootRef.value,
            () => sentinelRef.value,
        ],
        () => {
            if (resolveActive() && notificacoesStore.hasMore) {
                setup();
            } else {
                teardown();
            }
        },
        { flush: 'post' }
    );

    onBeforeUnmount(teardown);

    return {
        sentinelRef,
        setup,
        teardown,
    };
}
